import React from "react";
import { useTranslation } from "react-i18next";

import { UseCases } from "@planzone/core";
import { ProjectReport as P } from "@planzone/dto";
import {
  useAuthentication,
  useEditProjectReport,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectReport,
  useRetrieveProjectReportTemplate,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { MemberAvatar } from "@common/memberAvatar";
import { Modal } from "@common/modal";
import { ProjectRedirection } from "@common/projectRedirection";
import { FolderIcon } from "@common/folderIcon";
import { Button } from "@common/button";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { Props } from "./types";
import { IndicatorsForm } from "./indicatorsForm";
import { TextFieldsForm } from "./textFieldsForm";
import styles from "./styles.scss";

function ProjectReport({ project, reportId, close, isMultiProject }: Props) {
  const { t, i18n } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { account } = useAuthentication();
  const [isGeneratingPdf, setIsGeneratingPdf] = React.useState(false);
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    report,
    retrieveProjectReport,
  } = useRetrieveProjectReport(reportId);
  const {
    reportTemplates,
    isRequestPending: isTemplateRequestPending,
    isRequestSuccess: isTemplateRequestSuccess,
    isRequestFailure: isTemplateRequestFailure,
    retrieveProjectReportTemplate,
  } = useRetrieveProjectReportTemplate(planzone);
  const {
    template: criteriaTemplate,
    isRequestPending: isCriteriaRequestPending,
    isRequestSuccess: isCriteriaRequestSuccess,
    isRequestFailure: isCriteriaRequestFailure,
    retrieveSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "projectReport");
  const { isRequestPending: isEditRequestPending, editProjectReport } =
    useEditProjectReport(report);
  const [name, setName] = React.useState(report.name);
  const [textFieldsForm, setTextFieldsForm] = React.useState<P["fields"]>(
    report.fields.filter(({ type }) => type === "text"),
  );
  const [indicatorsForm, setIndicatorsForm] = React.useState<P["fields"]>(
    report.fields.filter(({ type }) => type === "indicator"),
  );
  const [attributesForm, setAttributesForm] = React.useState<{
    [key: string]: string;
  }>(report.attributes || {});

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectReport({ project, reportId });
  }, []);

  React.useEffect(() => {
    !isTemplateRequestPending &&
      !isTemplateRequestSuccess &&
      !isTemplateRequestFailure &&
      retrieveProjectReportTemplate(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    !isCriteriaRequestPending &&
      !isCriteriaRequestSuccess &&
      !isCriteriaRequestFailure &&
      retrieveSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "projectReport",
      });
  }, [planzone.uuid]);

  const resolvedTemplate =
    reportTemplates.find((t) => t.uuid === report.templateId) ||
    reportTemplates[0];

  React.useEffect(() => {
    setName(report.name);
    const textFields = report.fields.filter(({ type }) => type === "text");
    const hasCriteriaFields = textFields.some((f) => f.criteriaId);
    if (!hasCriteriaFields && resolvedTemplate) {
      const criteriaFields = resolvedTemplate.textFields
        .filter((f) => f.criteriaId)
        .map((f) => ({ ...f, value: "" }));
      setTextFieldsForm([...textFields, ...criteriaFields]);
    } else {
      setTextFieldsForm(textFields);
    }
    setIndicatorsForm(report.fields.filter(({ type }) => type === "indicator"));
    setAttributesForm(report.attributes || {});
  }, [report, reportTemplates]);

  function handleDownloadPdf() {
    setIsGeneratingPdf(true);

    fetch("/pdf/project-report", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${account.token}`,
      },
      body: JSON.stringify({
        projectUuid: project.uuid,
        reportUuid: reportId,
        language: i18n.language,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("PDF generation failed");
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${report.name || t("projectReport.title")}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {})
      .finally(() => setIsGeneratingPdf(false));
  }

  return (
    <Modal
      header={
        <div className={styles.header}>
          <h1>{t("projectReport.title")}</h1>
          <div className={styles.headerRight}>
            <div className={styles.projectRedirection}>
              {isMultiProject && (
                <ProjectRedirection
                  className={styles.contentProjectRedirection}
                  projectId={project.uuid}
                  close={close}
                />
              )}
            </div>
            <div className={styles.contextualMenu}>
              <SecureContextualMenu
                context={{ project }}
                items={[
                  {
                    name: t("projectReport.duplicate"),
                    useCase: UseCases.submitProjectReport,
                    act: () => {
                      close();
                      pushView({
                        key: UseCases.submitProjectReport,
                        data: { project, initialReport: report },
                      });
                    },
                  },
                  {
                    name: t("projectReport.remove"),
                    useCase: UseCases.removeProjectReport,
                    color: "#DB5F63",
                    act: () =>
                      pushView({
                        key: UseCases.removeProjectReport,
                        data: { project, projectReport: report },
                      }),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      }
      headerClassName={styles.headerBackGround}
      close={close}
    >
      <form
        className={styles.projectReport}
        onSubmit={(event) => {
          event.preventDefault();
          !isEditRequestPending &&
            editProjectReport({
              project,
              report,
              nextReport: {
                ...report,
                name:
                  name ||
                  t("projectReportSubmission.defaultTitle", {
                    date: new Date().toLocaleDateString(),
                  }),
                fields: [
                  ...indicatorsForm,
                  ...textFieldsForm.map((f) =>
                    f.criteriaId
                      ? { ...f, value: attributesForm[f.criteriaId] || "" }
                      : f,
                  ),
                ],
                attributes: attributesForm,
              },
            });
        }}
      >
        <div className={styles.main}>
          <input
            className={styles.input}
            placeholder={t("projectReportSubmission.namePlaceholder")}
            value={name}
            onChange={({ target }) => setName(target.value)}
          />
          <div className={styles.projectName}>
            <FolderIcon className={styles.folderIcon} />
            {project.name}
          </div>
          <div className={styles.row}>
            <p className={styles.submissionDate}>
              {t("projectReport.submissionDate")}{" "}
              {new Date(report.submissionDate).toLocaleDateString()}{" "}
              {t("projectReport.by")}
            </p>
            <div className={styles.author}>
              <div className={styles.authorAvatar}>
                <MemberAvatar avatarUrl="" name={report.author} />
              </div>
              <p className={styles.authorName}>{report.author}</p>
            </div>
          </div>
          <div className={styles.form}>
            <IndicatorsForm
              indicators={indicatorsForm}
              edit={setIndicatorsForm}
            />
          </div>
          <div className={styles.form}>
            <TextFieldsForm
              textFields={textFieldsForm}
              edit={setTextFieldsForm}
              secondaryCriteria={criteriaTemplate}
              attributesForm={attributesForm}
              onAttributeChange={(criteriaId, value) =>
                setAttributesForm((prev) => ({
                  ...prev,
                  [criteriaId]: value,
                }))
              }
              disabled={isEditRequestPending}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.downloadButton}
            press={handleDownloadPdf}
            isDisabled={isGeneratingPdf}
            status={isGeneratingPdf ? "pending" : "none"}
          >
            {t("projectReport.downloadPdf")}
          </Button>
          <Button
            type="submit"
            className={styles.button}
            status={isEditRequestPending ? "pending" : "none"}
            isDisabled={isEditRequestPending}
          >
            {t("projectReport.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectReport };
